import type { Worker } from 'tesseract.js';

/**
 * Starts a Tesseract.js worker that loads its runtime and English language
 * data from our own domain (`tools/vite-plugin-tesseract.ts`) instead of the
 * default CDN. `tesseract.js` is imported lazily so it's only downloaded when
 * OCR runs. Callers must `terminate()` the worker when done to free its memory.
 *
 * Tesseract caches the language data in IndexedDB (Firebase serves `.gz` files
 * with `no-store`). The versioned assets URL is used as the cache key so a data
 * upgrade isn't shadowed by an old cached copy.
 *
 * Rejects as soon as Tesseract.js reports an error while starting.
 */
export async function createOcrWorker(): Promise<Worker> {
  const { createWorker, OEM } = await import('tesseract.js');
  const assetsURL = import.meta.env.TESSERACT_ASSETS_URL;

  let rejectOnError: (error: Error) => void = () => {};
  const failed = new Promise<never>((_, reject) => {
    rejectOnError = reject;
  });

  return Promise.race([
    createWorker('eng', OEM.LSTM_ONLY, {
      workerPath: `${assetsURL}worker.min.js`,
      corePath: assetsURL,
      langPath: assetsURL,
      cachePath: assetsURL,
      workerBlobURL: false,
      errorHandler: (error: unknown) => rejectOnError(new Error(String(error))),
    }),
    failed,
  ]);
}
